import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {API_URL} from '../api-config';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft } from "lucide-react";
import { handleAuthError } from '../hooks/ValidateToken';

const Perfil = () => {
  const [user, setUser] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    phone_number: ''
  });
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${API_URL}/user/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setUser(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to fetch user');
        handleAuthError(err, navigate);
      }
    };

    fetchUser();
  }, []);

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(
        `${API_URL}/user/me`,
        {
          username: user.username,
          email: user.email,
          first_name: user.first_name,
          last_name: user.last_name,
          phone_number: user.phone_number
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSuccess('User updated successfully!');
      setUser(response.data);
      setError('');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to update user';
      handleAuthError(err, navigate);
      if (typeof errorMessage === 'object') {
        setError(JSON.stringify(errorMessage));
      } else {
        setError(errorMessage);
      }
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API_URL}/user/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setPasswordSuccess('Password updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password');
    }
  };

  return (
    <div
      className="flex flex-col md:flex-row justify-around bg-slate-900 min-h-screen p-4 md:p-12 text-white"
      role="main"
      aria-label="Perfil de usuario y cambio de contraseña"
    >
      <button
          onClick={() => navigate('/search')}
          className="fixed top-6 left-6 z-20 bg-purple-600 text-white px-4 py-2 rounded-full hover:bg-purple-700 transition-all duration-300 shadow-lg 
                    sm:text-base text-sm sm:px-4 sm:py-2"
          aria-label="Volver a la página anterior"
        >
          {/* Icono */}
          <ArrowLeft className="w-5 h-5 inline-block mr-2 sm:mr-2" />
          {/* Texto (visible solo en pantallas grandes) */}
          <span className="hidden sm:inline">Volver</span>
        </button>
  
      <section
        className="w-full md:w-1/2 max-w-lg p-6 space-y-6 bg-slate-800 rounded-lg shadow-lg border border-purple-400 mb-6 md:mb-0 md:mr-6"
        aria-labelledby="user-info-title"
        role="region"
      >
        <h2
          id="user-info-title"
          className="text-2xl md:text-3xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-purple-200"
        >
          Información del Usuario
        </h2>
        {error && (
          <div
            className="p-2 text-sm text-red-300 bg-red-900/20 rounded"
            role="alert"
          >
            {typeof error === 'object' ? JSON.stringify(error) : error}
          </div>
        )}
        {success && (
          <div
            className="p-2 text-sm text-green-300 bg-green-900/20 rounded"
            role="status"
          >
            {success}
          </div>
        )}
        {user && (
          <form onSubmit={(e) => handleUpdateUser(e)} aria-label="Formulario de actualización de usuario">
            {/* Campo de Nombre de Usuario */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-white">
                Nombre de Usuario
              </label>
              <input
                id="username"
                type="text"
                value={user.username}
                onChange={(e) => setUser({ ...user, username: e.target.value })}
                className="w-full px-4 py-2 mt-1 text-white bg-slate-700 border border-purple-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-slate-600"
                aria-describedby="username-helper"
              />
              <p id="username-helper" className="text-xs text-gray-400">
                Este es tu nombre de usuario público.
              </p>
            </div>
  
            {/* Campo de Email (solo lectura) */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={user.email}
                readOnly
                className="w-full px-4 py-2 mt-1 text-gray-400 bg-slate-700 border border-purple-400 rounded-lg cursor-not-allowed"
                aria-readonly="true"
              />
            </div>
  
            {/* Campo de Nombre */}
            <div>
              <label htmlFor="first-name" className="block text-sm font-medium text-white">
                Nombre
              </label>
              <input
                id="first-name"
                type="text"
                value={user.first_name || ''}
                onChange={(e) => setUser({ ...user, first_name: e.target.value })}
                className="w-full px-4 py-2 mt-1 text-white bg-slate-700 border border-purple-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-slate-600"
              />
            </div>
  
            {/* Campo de Apellido */}
            <div>
              <label htmlFor="last-name" className="block text-sm font-medium text-white">
                Apellido
              </label>
              <input
                id="last-name"
                type="text"
                value={user.last_name || ''}
                onChange={(e) => setUser({ ...user, last_name: e.target.value })}
                className="w-full px-4 py-2 mt-1 text-white bg-slate-700 border border-purple-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-slate-600"
              />
            </div>
  
            {/* Campo de Teléfono */}
            <div>
              <label htmlFor="phone-number" className="block text-sm font-medium text-white">
                Teléfono
              </label>
              <input
                id="phone-number"
                type="text"
                value={user.phone_number || ''}
                onChange={(e) => setUser({ ...user, phone_number: e.target.value })}
                className="w-full px-4 py-2 mt-1 text-white bg-slate-700 border border-purple-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-slate-600"
              />
            </div>
  
            {/* Botón de Actualización */}
            <button
              type="submit"
              className="w-full px-4 py-2 mt-4 text-white bg-purple-600 rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
              aria-label="Actualizar información del usuario"
            >
              Actualizar Usuario
            </button>
          </form>
        )}
      </section>
  
      {/* Cambio de Contraseña */}
      <section
        className="w-full md:w-1/2 max-w-lg p-6 space-y-6 bg-slate-800 rounded-lg shadow-lg border border-purple-400 md:ml-6"
        aria-labelledby="change-password-title"
        role="region"
      >
        <h2
          id="change-password-title"
          className="text-2xl md:text-3xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-purple-200"
        >
          Cambiar Contraseña
        </h2>
        {passwordSuccess && (
          <div
            className="p-2 text-sm text-green-300 bg-green-900/20 rounded"
            role="status"
          >
            {passwordSuccess}
          </div>
        )}
  
        {/* Campo para la Contraseña Actual */}
        <div>
          <label htmlFor="current-password" className="block text-sm font-medium text-white">
            Contraseña Actual
          </label>
          <div className="relative flex">
            <input
              id="current-password"
              type={showCurrentPassword ? "text" : "password"}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full px-4 py-2 mt-1 text-white bg-slate-700 border border-purple-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-slate-600"
              aria-describedby="current-password-helper"
            />
            <button
              type="button"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5 px-2 rounded-lg text-white"
              aria-label={showCurrentPassword ? "Ocultar contraseña actual" : "Mostrar contraseña actual"}
            >
              {showCurrentPassword ? <EyeOff className="h-5 w-5 mt-1" /> : <Eye className="h-5 w-5 mt-1" />}
            </button>
          </div>
          <p id="current-password-helper" className="text-xs text-gray-400">
            Introduce tu contraseña actual para verificar tu identidad.
          </p>
        </div>
  
        {/* Campo para la Nueva Contraseña */}
        <div>
          <label htmlFor="new-password" className="block text-sm font-medium text-white">
            Nueva Contraseña
          </label>
          <div className="relative flex">
            <input
              id="new-password"
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-2 mt-1 text-white bg-slate-700 border border-purple-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-slate-600"
              aria-describedby="new-password-helper"
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5 px-2 rounded-lg text-white"
              aria-label={showNewPassword ? "Ocultar nueva contraseña" : "Mostrar nueva contraseña"}
            >
              {showNewPassword ? <EyeOff className="h-5 w-5 mt-1" /> : <Eye className="h-5 w-5 mt-1" />}
            </button>
          </div>
          <p id="new-password-helper" className="text-xs text-gray-400">
            Asegúrate de usar una contraseña segura.
          </p>
        </div>
  
        {/* Botón de Cambio de Contraseña */}
        <button
          onClick={handleChangePassword}
          className="w-full px-4 py-2 mt-4 text-white bg-purple-600 rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
          aria-label="Cambiar contraseña"
        >
          Cambiar Contraseña
        </button>
      </section>
    </div>
  );
};

export default Perfil;
